import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Button,
  ModalHeader,
  ModalFooter,
  Icon,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { KText } from '@/ui-library/components/text';
import { useHrisIntegrations } from '@/components/ats/hris/useHrisIntegrations';
import { useSelector } from 'react-redux';
import { usePushToHris } from '@/components/ats/hris/useHrisIntegrations';
import { UsersPlus } from '@kula-ai/icons/outline/UsersPlus';
import { useState, useRef } from 'react';
import EmployeeFormDrawer from '@/components/ats/hris/EmployeeFormDrawer';
import { ChevronDown } from '@kula-ai/icons-core';

const AddToHRISModal = ({
  modalActions,
  applicationData,
  refetch = () => {},
}) => {
  const { getUnpushedIntegrations, renderIcon } = useHrisIntegrations();
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const employeeDrawerActions = useDisclosure();
  const [formConfig, setFormConfig] = useState([]);
  const [currentIntegration, setCurrentIntegration] = useState(null);
  const [currentAppId, setCurrentAppId] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const formRef = useRef(null);

  const settings = useSelector((state) => state.bootstrap?.settings);
  const activeIntegrations =
    settings?.account?.account_integrations?.hris_integrations || [];

  const pushed = applicationData?.pushed_to_hris || [];

  const unpushedIntegrations = getUnpushedIntegrations(
    activeIntegrations,
    pushed
  );

  const contact = applicationData?.contact || {};
  const fullName = `${contact?.first_name || contact?.name || ''} ${
    contact?.last_name || ''
  }`.trim();

  const { handlePushToHRIS, isPending, isFetchingForm } = usePushToHris({
    onSuccess: () => {
      modalActions?.onClose();
      setShowEmployeeForm(false);
    },
  });

  const handleCancel = () => {
    modalActions?.onClose();
    setShowEmployeeForm(false);
    employeeDrawerActions.onClose();
    refetch();
  };

  const handleSuccess = () => {
    setShowEmployeeForm(false);
    employeeDrawerActions.onClose();
    modalActions?.onClose();
    refetch();
  };

  const handlePush = async (integration) => {
    await handlePushToHRIS(integration, applicationData, {
      onFormReady: ({ formSchema, mappedValues }) => {
        setInitialValues(mappedValues);
        setFormConfig(formSchema);
        setCurrentIntegration(integration);
        setCurrentAppId(applicationData?.id);
        setShowEmployeeForm(true);
        employeeDrawerActions.onOpen();
      },
      onDirectSuccess: () => {
        modalActions?.onClose();
        refetch();
      },
    });
  };

  const getLoadingText = (integration) => {
    return isFetchingForm
      ? 'Loading employee form'
      : `Adding to ${integration.name}`;
  };

  const renderContent = () => {
    if (unpushedIntegrations.length === 1) {
      const integration = unpushedIntegrations[0];
      return (
        <>
          <ModalHeader>
            <Flex gap="6px" alignItems="center">
              {renderIcon(integration)}
              <KText
                color="secondary-900"
                size="md-bold"
                wordBreak="break-word"
              >
                Add {fullName} to {integration.name}
              </KText>
            </Flex>
          </ModalHeader>
          <ModalBody py="0">
            <KText>
              Create {fullName} as a new employee on {integration.name}.
            </KText>
          </ModalBody>
          <ModalFooter>
            <Flex gap="6px" flexDir="row-reverse">
              <Button
                size="sm"
                variant="primary"
                isLoading={isFetchingForm || isPending}
                loadingText={getLoadingText(integration)}
                onClick={() => handlePush(integration)}
              >
                Add to {integration.name}
              </Button>
              <Button size="sm" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </Flex>
          </ModalFooter>
        </>
      );
    } else {
      return (
        <>
          <ModalHeader>
            <Flex gap="6px" alignItems="center">
              <Icon as={UsersPlus} w="20px" h="20px" />
              <KText
                color="secondary-900"
                size="md-bold"
                wordBreak="break-word"
              >
                Add {fullName} to HRIS
              </KText>
            </Flex>
          </ModalHeader>
          <ModalBody py="0">
            <KText>Create {fullName} to your HRIS as a new employee</KText>
          </ModalBody>
          <ModalFooter>
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="primary"
                isLoading={isFetchingForm || isPending}
                loadingText={
                  isFetchingForm
                    ? 'Loading employee form'
                    : `Adding to ${currentIntegration?.name}`
                }
                rightIcon={<ChevronDown size={16} />}
              >
                Add to HRIS
              </MenuButton>
              <MenuList zIndex={9}>
                {unpushedIntegrations.map((integration) => (
                  <MenuItem
                    key={integration.type}
                    onClick={() => {
                      setCurrentIntegration(integration);
                      handlePush(integration);
                    }}
                  >
                    <Flex gap="8px" alignItems="center">
                      {renderIcon(integration, {
                        width: '16px',
                        height: '16px',
                      })}
                      <KText>Add to {integration.name}</KText>
                    </Flex>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <Button
              size="sm"
              variant="secondary"
              ml="8px"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </ModalFooter>
        </>
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={modalActions?.isOpen && !showEmployeeForm}
        onClose={modalActions?.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <Box
            position="absolute"
            right="10px"
            top="10px"
            cursor="pointer"
            onClick={handleCancel}
          >
            <Icon as={XClose} boxSize="20px" color="secondary-800" />
          </Box>
          {renderContent()}
        </ModalContent>
      </Modal>
      <EmployeeFormDrawer
        isOpen={employeeDrawerActions.isOpen && showEmployeeForm}
        onClose={() => {
          employeeDrawerActions.onClose();
          setShowEmployeeForm(false);
          modalActions?.onClose();
          refetch();
        }}
        integrationName={currentIntegration?.name || ''}
        integration={currentIntegration}
        applicationId={currentAppId}
        formConfig={formConfig}
        onSuccess={handleSuccess}
        formRef={formRef}
        initialValues={initialValues}
      />
    </>
  );
};

export default AddToHRISModal;
